import { useState, useEffect, useCallback, useMemo } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Menu, TrendingUp } from "lucide-react";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import ClientList from "./components/ClientList";
import ClientReport from "./components/ClientReport";
import ManualEntry from "./components/ManualEntry";
import ImportData from "./components/ImportData";
import Reports from "./components/Reports";
import AdCreative from "./components/AdCreative";
import PublicReport from "./components/PublicReport";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [clients, setClients] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [adMetrics, setAdMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [currentView, setCurrentView] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchClients = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.get(`${API}/clients`);
        setClients(response.data);
        return;
      } catch (e) {
        console.error(`Error fetching clients (attempt ${attempt + 1}):`, e);
        if (attempt === 2) {
          toast.error("Failed to fetch clients. Please refresh the page.");
        } else {
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    }
  }, []);

  const fetchMetrics = useCallback(async (clientId = null) => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API}/metrics`, { params });
      setMetrics(response.data);
    } catch (e) {
      console.error("Error fetching metrics:", e);
    }
  }, []);

  const fetchAdMetrics = useCallback(async (clientId = null) => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API}/ad-metrics`, { params });
      setAdMetrics(response.data);
    } catch (e) {
      console.error("Error fetching ad metrics:", e);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClients(), fetchMetrics(), fetchAdMetrics()]);
      setLoading(false);
    };
    loadData();
  }, [fetchClients, fetchMetrics, fetchAdMetrics]);

  const singleClients = useMemo(() => clients.filter(c => c.client_type !== 'multi'), [clients]);
  const multiClients = useMemo(() => clients.filter(c => c.client_type === 'multi'), [clients]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    const detailView = client.client_type === 'multi' ? 'client-detail-multi' : 'client-detail-single';
    setCurrentView(detailView);
    fetchMetrics(client.id);
    fetchAdMetrics(client.id);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === "dashboard" || view === "clients-single" || view === "clients-multi") {
      setSelectedClient(null);
      fetchMetrics();
      fetchAdMetrics();
    }
  };

  const refreshData = async () => {
    await Promise.all([
      fetchClients(),
      fetchMetrics(selectedClient?.id),
      fetchAdMetrics(selectedClient?.id),
    ]);
  };

  const renderContent = () => {
    switch (currentView) {
      case "dashboard":
        return (
          <Dashboard
            clients={clients}
            metrics={metrics}
            adMetrics={adMetrics}
            onClientSelect={handleClientSelect}
            loading={loading}
          />
        );
      case "clients":
      case "clients-single":
        return (
          <ClientList
            clients={singleClients}
            onClientSelect={handleClientSelect}
            onRefresh={fetchClients}
            API={API}
            clientType="single"
          />
        );
      case "clients-multi":
        return (
          <ClientList
            clients={multiClients}
            onClientSelect={handleClientSelect}
            onRefresh={fetchClients}
            API={API}
            clientType="multi"
          />
        );
      case "client-detail":
      case "client-detail-single":
        return selectedClient ? (
          <ClientReport
            client={selectedClient}
            metrics={metrics}
            adMetrics={adMetrics}
            onBack={() => handleViewChange("clients-single")}
            onRefresh={refreshData}
            onViewChange={handleViewChange}
            API={API}
          />
        ) : (
          <ClientList
            clients={singleClients}
            onClientSelect={handleClientSelect}
            onRefresh={fetchClients}
            API={API}
            clientType="single"
          />
        );
      case "client-detail-multi":
        return selectedClient ? (
          <ClientReport
            client={selectedClient}
            metrics={metrics}
            adMetrics={adMetrics}
            onBack={() => handleViewChange("clients-multi")}
            onRefresh={refreshData}
            onViewChange={handleViewChange}
            API={API}
          />
        ) : (
          <ClientList
            clients={multiClients}
            onClientSelect={handleClientSelect}
            onRefresh={fetchClients}
            API={API}
            clientType="multi"
          />
        );
      case "manual-entry":
        return (
          <ManualEntry
            clients={clients}
            onSave={refreshData}
            API={API}
          />
        );
      case "import":
        return (
          <ImportData
            clients={clients}
            onImport={refreshData}
            API={API}
          />
        );
      case "reports":
        return (
          <Reports
            clients={clients}
            metrics={metrics}
            adMetrics={adMetrics}
            API={API}
          />
        );
      case "ad-creative":
        return (
          <AdCreative
            clients={clients}
            API={API}
          />
        );
      default:
        return <Dashboard clients={clients} metrics={metrics} adMetrics={adMetrics} loading={loading} />;
    }
  };

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/report/:token" element={<PublicReport API={API} />} />
        <Route path="*" element={
          <div className="flex min-h-screen bg-slate-50" data-testid="app-container">
            <Sidebar
              currentView={currentView}
              onViewChange={handleViewChange}
              clientCount={clients.length}
              singleCount={singleClients.length}
              multiCount={multiClients.length}
              mobileOpen={sidebarOpen}
              onMobileClose={() => setSidebarOpen(false)}
            />
            <main className="flex-1 lg:ml-64 p-4 md:p-6 lg:p-8 min-w-0">
              {/* Mobile top bar (hamburger + logo) */}
              <div className="lg:hidden flex items-center justify-between mb-4 -mx-1">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-700 shadow-sm active:scale-95 transition-transform"
                  data-testid="mobile-menu-btn"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-base font-bold text-slate-900 font-['Outfit']">MetricFlow</span>
                </div>
                <div className="w-10" />
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentView}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {renderContent()}
                </motion.div>
              </AnimatePresence>
            </main>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
